import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Mountain, X, Loader2, Plus } from "lucide-react";

/**
 * WaypointAutocomplete — multi-select search across all Italian places (any type).
 * Props:
 *  - values: string[] (display labels)
 *  - onChange(values: string[])
 *  - testId
 */
export default function WaypointAutocomplete({ values = [], onChange, testId = "waypoint-autocomplete" }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const search = async (q) => {
    if (!q || q.trim().length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/geocode/search", {
        params: { q: q.trim(), limit: 10, only_settlements: false },
      });
      setResults(data);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const onInput = (e) => {
    const v = e.target.value;
    setQuery(v);
    setOpen(true);
    setHighlight(-1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(v), 280);
  };

  const add = (label, lat, lng) => {
    const clean = (label || "").trim();
    if (!clean || values.some((v) => (typeof v === "string" ? v : v?.name) === clean)) {
      setQuery(""); setResults([]); setOpen(false);
      return;
    }
    const item = (typeof lat === "number" && typeof lng === "number")
      ? { name: clean, lat, lng }
      : { name: clean };
    onChange([...values, item]);
    setQuery("");
    setResults([]);
    setOpen(false);
  };

  const remove = (target) => onChange(values.filter((v) => (typeof v === "string" ? v : v?.name) !== (typeof target === "string" ? target : target?.name)));

  const onKey = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (open && highlight >= 0 && results[highlight]) {
        const r = results[highlight];
        add(r.label, r.lat, r.lng);
      } else if (query.trim().length >= 2) {
        // Allow free-text custom waypoint (no coords)
        add(query.trim());
      }
    } else if (e.key === "ArrowDown" && open) {
      e.preventDefault(); setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === "ArrowUp" && open) {
      e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={containerRef} className="relative">
      {values.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {values.map((w) => {
            const name = typeof w === "string" ? w : w?.name;
            const hasCoords = typeof w === "object" && typeof w?.lat === "number";
            return (
              <span key={name} className="chip" style={{ background: "rgba(255,59,48,0.15)", color: "#E11D48", borderColor: "rgba(255,59,48,0.4)" }}>
                <Mountain className="w-3 h-3" /> {name}{hasCoords ? "" : ""}
                <button type="button" data-testid={`${testId}-remove-${name}`} onClick={() => remove(w)} className="hover:opacity-70 ml-1">
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      <div className="relative">
        <input
          data-testid={testId}
          type="text"
          value={query}
          onChange={onInput}
          onFocus={() => { setOpen(true); if (results.length === 0 && query.length >= 2) search(query); }}
          onKeyDown={onKey}
          placeholder="Cerca passi, laghi, paesi, salite... (premi Invio per aggiungere)"
          autoComplete="off"
          className="w-full bg-gray-50 border border-gray-200 focus:border-[#E11D48] focus:outline-none rounded-sm px-4 py-3 pr-10 text-sm text-gray-900"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 animate-spin" />}
      </div>

      {open && query.length >= 2 && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-sm shadow-xl max-h-72 overflow-y-auto">
          {!loading && results.length === 0 && (
            <button
              type="button"
              data-testid={`${testId}-add-custom`}
              onClick={() => add(query.trim())}
              className="w-full text-left px-4 py-3 hover:bg-gray-100 flex items-center gap-2 text-sm text-gray-800"
            >
              <Plus className="w-4 h-4 text-[#E11D48]" />
              Aggiungi "{query.trim()}"
            </button>
          )}
          {results.map((r, i) => (
            <button
              type="button"
              key={r.id}
              data-testid={`${testId}-opt-${i}`}
              onMouseEnter={() => setHighlight(i)}
              onClick={() => add(r.label, r.lat, r.lng)}
              className={`w-full text-left px-4 py-2.5 border-b border-gray-100 last:border-b-0 ${i === highlight ? "bg-gray-100" : ""}`}
            >
              <div className="text-sm text-gray-900 font-medium leading-tight">{r.name}</div>
              {r.label !== r.name && (
                <div className="text-[11px] text-gray-500 mt-0.5 truncate">{r.label}</div>
              )}
              {r.type && (
                <div className="text-[10px] uppercase tracking-widest text-gray-400 mt-0.5">{r.type}</div>
              )}
            </button>
          ))}
        </div>
      )}

      <p className="text-xs text-gray-500 mt-2">
        Cerca tra tutti i luoghi italiani. Puoi anche aggiungere un nome personalizzato premendo Invio.
      </p>
    </div>
  );
}
